import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import type {
  AssistanceWarning,
  MissingRequiredField,
  ReusedRecord,
} from '@/api/schemas/ai-assistance';

/**
 * Everything the proposal says ABOUT itself.
 *
 * ---------------------------------------------------------------------------
 * THREE LISTS, THREE MEANINGS
 * ---------------------------------------------------------------------------
 *   - REUSED records already exist: this is what the proposal would use
 *     instead of creating a duplicate.
 *   - WARNINGS: the proposal may be wrong, and the code says in which way.
 *   - MISSING REQUIRED FIELDS: a person must still supply these. The model
 *     could not, and must not, invent them.
 *
 * The warning code is shown next to the message because it is a CLOSED
 * vocabulary: `possible_duplicate` and `outside_requested_scope` are different
 * problems, and the message alone does not say which one this is.
 */
export type ReviewNoticesProps = {
  readonly reused: ReadonlyArray<ReusedRecord>;
  readonly warnings: ReadonlyArray<AssistanceWarning>;
  readonly missing: ReadonlyArray<MissingRequiredField>;
};

export function ReviewNotices({ reused, warnings, missing }: ReviewNoticesProps): ReactNode {
  if (reused.length === 0 && warnings.length === 0 && missing.length === 0) return null;

  return (
    <div className="space-y-3">
      {reused.length === 0 ? null : (
        <section className="rounded-lg border border-border bg-surface-raised p-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-content-muted">
            Already exists — reuse it
          </h3>
          <ul className="mt-2 space-y-1">
            {reused.map((record) => (
              <li key={record.id} className="text-sm text-content">
                <span className="font-medium">{record.name}</span>
                <span className="text-content-muted"> — {record.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {warnings.length === 0 ? null : (
        <section className="rounded-lg border border-border bg-surface-raised p-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-content-muted">
            Worth checking
          </h3>
          <ul className="mt-2 space-y-2">
            {warnings.map((warning) => (
              <li key={`${warning.code}-${warning.field ?? warning.message}`} className="text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="caution">{warning.code}</Badge>
                  {warning.field ? (
                    <span className="text-xs text-content-subtle">{warning.field}</span>
                  ) : null}
                </div>
                <p className="text-content">{warning.message}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {missing.length === 0 ? null : (
        <section className="rounded-lg border border-caution bg-caution-soft p-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-content">
            You still need to supply this
          </h3>
          <ul className="mt-2 space-y-1">
            {missing.map((field) => (
              <li key={field.field} className="text-sm text-content">
                <span className="font-medium">{field.field}</span>
                <span className="text-content-muted"> — {field.reason}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
